import React from 'react';
import { Box, Typography, Button, IconButton, Stack, Paper, Divider, Chip } from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { QuestionOption, EmojiType } from '../types';
import { MultiLanguageInput } from './MultiLanguageInput';
import { OptionColorPicker } from './OptionColorPicker';
import { OptionEmojiPicker } from './OptionEmojiPicker';
import { isValidHexColor } from '../utils/colorValidation';
import { generateRandomColor } from '../utils/colorUtils';

interface QuestionOptionsEditorProps {
  options: QuestionOption[];
  onChange: (options: QuestionOption[]) => void;
  disabled?: boolean;
  minOptions?: number;
  maxOptions?: number;
  questionType: 'SINGLE_SELECT' | 'MULTI_SELECT';
}

type QuestionOptionValue = string | Record<string, string> | number | EmojiType | undefined;

interface SortableOptionItemProps {
  option: QuestionOption;
  index: number;
  onUpdate: (optionId: string, field: keyof QuestionOption, value: QuestionOptionValue) => void;
  onRemove: (optionId: string) => void;
  disabled?: boolean;
  canRemove: boolean;
  isValidOption: (option: QuestionOption) => boolean;
}

const SortableOptionItem: React.FC<SortableOptionItemProps> = ({
  option,
  index,
  onUpdate,
  onRemove,
  disabled = false,
  canRemove,
  isValidOption,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: option.id,
    disabled: disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : 1,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      variant="outlined"
      sx={{
        p: 2,
        position: 'relative',
        overflow: 'visible',
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={2}>
        {/* Drag Handle */}
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pt: 1,
            cursor: disabled ? 'default' : 'grab',
            '&:active': { cursor: disabled ? 'default' : 'grabbing' },
          }}
        >
          <Box
            sx={{
              p: 0.5,
              borderRadius: '4px',
              backgroundColor: 'action.hover',
              '&:hover': { backgroundColor: disabled ? 'action.hover' : 'action.selected' },
            }}
          >
            <DragIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            {index + 1}
          </Typography>
        </Box>

        {/* Option Content with Integrated Color and Emoji Pickers */}
        <Box sx={{ flexGrow: 1 }}>
          <MultiLanguageInput
            value={option.label}
            onChange={(label) => onUpdate(option.id, 'label', label)}
            placeholder="Enter option text"
            required
            disabled={disabled}
            error={!isValidOption(option)}
            requiredLanguages={['no']}
            headerAction={
              <Stack direction="row" spacing={0.5} alignItems="center">
                <OptionColorPicker
                  value={option.color}
                  onChange={(color) => onUpdate(option.id, 'color', color)}
                  disabled={disabled}
                  optionIndex={index}
                />
                <OptionEmojiPicker
                  value={option.emoji}
                  onChange={(emoji) => onUpdate(option.id, 'emoji', emoji)}
                  disabled={disabled}
                  optionIndex={index}
                  color={option.color}
                />
              </Stack>
            }
          />
        </Box>

        {/* Remove Button */}
        <Box sx={{ pt: 1 }}>
          <IconButton
            color="error"
            size="small"
            onClick={() => onRemove(option.id)}
            disabled={disabled || !canRemove}
            sx={{ opacity: !canRemove ? 0.3 : 1 }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Stack>
    </Paper>
  );
};

export const QuestionOptionsEditor: React.FC<QuestionOptionsEditorProps> = ({
  options,
  onChange,
  disabled = false,
  minOptions = 2,
  maxOptions = 10,
  questionType,
}) => {
  const generateOptionId = () => `option_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const addOption = () => {
    if (options.length >= maxOptions) return;

    const newOption: QuestionOption = {
      id: generateOptionId(),
      label: { en: '' },
      value: `option_${options.length + 1}`,
      order: options.length + 1,
      color: generateRandomColor(),
    };

    onChange([...options, newOption]);
  };

  const removeOption = (optionId: string) => {
    if (options.length <= minOptions) return;

    const updatedOptions = options
      .filter((option) => option.id !== optionId)
      .map((option, index) => ({
        ...option,
        order: index + 1,
        value: `option_${index + 1}`,
      }));

    onChange(updatedOptions);
  };

  const updateOption = (
    optionId: string,
    field: keyof QuestionOption,
    value: QuestionOptionValue
  ) => {
    const updatedOptions = options.map((option) =>
      option.id === optionId ? { ...option, [field]: value } : option
    );
    onChange(updatedOptions);
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before activating
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = options.findIndex((option) => option.id === active.id);
    const newIndex = options.findIndex((option) => option.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Create new options array with updated order using arrayMove
    const reorderedOptions = arrayMove(options, oldIndex, newIndex);

    // Update order and value to match array indices
    const optionsWithNewOrder = reorderedOptions.map((option, index) => ({
      ...option,
      order: index + 1,
      value: `option_${index + 1}`,
    }));

    onChange(optionsWithNewOrder);
  };

  const isValidOption = (option: QuestionOption) => {
    // Check label has English text
    const hasValidLabel = option.label.en?.trim().length > 0;

    // Check color format if color is provided
    const hasValidColor = isValidHexColor(option.color);

    return hasValidLabel && hasValidColor;
  };

  const getValidationMessage = () => {
    const invalidOptions = options.filter((option) => !isValidOption(option));
    if (invalidOptions.length > 0) {
      const missingLabels = invalidOptions.filter((opt) => !opt.label.en?.trim());
      const invalidColors = invalidOptions.filter((opt) => !isValidHexColor(opt.color));

      const messages = [];
      if (missingLabels.length > 0) {
        messages.push(`${missingLabels.length} option(s) missing English text`);
      }
      if (invalidColors.length > 0) {
        messages.push(`${invalidColors.length} option(s) with invalid color format`);
      }
      return messages.join(', ');
    }
    if (options.length < minOptions) {
      return `At least ${minOptions} options required`;
    }
    return null;
  };

  const validationMessage = getValidationMessage();

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="subtitle1" fontWeight={600}>
          {questionType === 'SINGLE_SELECT' ? 'Single Choice Options' : 'Multiple Choice Options'}
        </Typography>
        <Chip
          label={`${options.length}/${maxOptions}`}
          size="small"
          color={options.length >= minOptions ? 'success' : 'warning'}
          variant="outlined"
        />
      </Stack>

      <Stack spacing={2}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={options.map((option) => option.id)}
            strategy={verticalListSortingStrategy}
          >
            <Stack spacing={2}>
              {options.map((option, index) => (
                <SortableOptionItem
                  key={option.id}
                  option={option}
                  index={index}
                  onUpdate={updateOption}
                  onRemove={removeOption}
                  disabled={disabled}
                  canRemove={options.length > minOptions}
                  isValidOption={isValidOption}
                />
              ))}
            </Stack>
          </SortableContext>
        </DndContext>

        {/* Add Option Button */}
        <Box>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addOption}
            disabled={disabled || options.length >= maxOptions}
            fullWidth
            sx={{
              py: 1.5,
              borderStyle: 'dashed',
              '&:hover': {
                borderStyle: 'dashed',
              },
            }}
          >
            Add Option ({options.length}/{maxOptions})
          </Button>
        </Box>

        {/* Validation Message */}
        {validationMessage && (
          <Box>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="body2" color="error" textAlign="center">
              {validationMessage}
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
};
